import { useState, useCallback, useMemo, useEffect } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { MultiSelect } from '@/components/MultiSelect';
import { Dropdownlist } from '@/components/Dropdownlist';
import { DateTimePicker } from '@/components/DateTimePicker';
import { Codes } from '@/constants/codes';
import { api } from '@/services/api-class';

interface CaseReportIncidentDetailsProps {
  report: any;
  isReadOnly?: boolean;
  natureOfCallOptions?: any[];
  natureOfCallGroupBy?: string | null;
  onNatureChange?: (value: any) => void;
  onFieldChange?: (fieldName: string, value?: any) => void;
}

export function CaseReportIncidentDetails({
  report,
  isReadOnly = false,
  natureOfCallOptions = [],
  natureOfCallGroupBy = null,
  onNatureChange,
  onFieldChange,
}: CaseReportIncidentDetailsProps) {
  const { theme } = useTheme();

  const [occurredFromOpen, setOccurredFromOpen] = useState(false);
  const [occurredToOpen, setOccurredToOpen] = useState(false);
  const [notifiedTimeOpen, setNotifiedTimeOpen] = useState(false);
  const [receivedViaOptions, setReceivedViaOptions] = useState<any[]>([]);
  const [receivedByOptions, setReceivedByOptions] = useState<any[]>([]);
  const [employeeRoleOptions, setEmployeeRoleOptions] = useState<any[]>([]);

  // Load dropdown options on mount
  useEffect(() => {
    // Load received via options
    if (typeof Codes !== 'undefined' && Codes.getHowReceivedCodes) {
      setReceivedViaOptions(Codes.getHowReceivedCodes());
    }
    // Load notified person as options
    if (typeof Codes !== 'undefined' && Codes.getNotifiedPersonAsCodesWithOtherLast) {
      setEmployeeRoleOptions(Codes.getNotifiedPersonAsCodesWithOtherLast());
    }
    // Load available officers for received by dropdown
    if (typeof api !== 'undefined' && api.getAvailableMDTOfficers) {
      api.getAvailableMDTOfficers((result: any) => {
        setReceivedByOptions(
          result.officerList
            .map((o: any) => ({
              id: o.id,
              name: `${o.firstName} ${o.lastName}`,
            }))
            .sort((a: any, b: any) => a.name.localeCompare(b.name))
        );
      });
    }

    const handleOtherPickerOpened = (event: any) => {
      if (event.detail && event.detail.source !== 'incident-details') {
        setOccurredFromOpen(false);
        setOccurredToOpen(false);
        setNotifiedTimeOpen(false);
      }
    };

    window.addEventListener('datetime-picker-opened', handleOtherPickerOpened);
    return () => {
      window.removeEventListener('datetime-picker-opened', handleOtherPickerOpened);
    };
  }, []);

  const handleNatureChange = useCallback((value: any) => {
    onNatureChange?.(value);
  }, [onNatureChange]);

  const handleFieldChangeCallback = useCallback((fieldName: string) => {
    onFieldChange?.(fieldName);
  }, [onFieldChange]);

  const handleOccurredFromChange = useCallback((val: any) => {
    onFieldChange?.('incidentOccurredFrom', val);
  }, [onFieldChange]);

  const handleOccurredToChange = useCallback((val: any) => {
    onFieldChange?.('incidentOccurredTo', val);
  }, [onFieldChange]);

  const handleNotifiedTimeChange = useCallback((val: any) => {
    onFieldChange?.('notifiedOfIncidentTime', val);
  }, [onFieldChange]);

  const handleOccurredFromOpen = useCallback(() => {
    setOccurredToOpen(false);
    setNotifiedTimeOpen(false);
    window.dispatchEvent(new CustomEvent('datetime-picker-opened', { detail: { source: 'incident-details' } }));
  }, []);

  const handleOccurredToOpen = useCallback(() => {
    setOccurredFromOpen(false);
    setNotifiedTimeOpen(false);
    window.dispatchEvent(new CustomEvent('datetime-picker-opened', { detail: { source: 'incident-details' } }));
  }, []);

  const handleNotifiedTimeOpen = useCallback(() => {
    setOccurredFromOpen(false);
    setOccurredToOpen(false);
    window.dispatchEvent(new CustomEvent('datetime-picker-opened', { detail: { source: 'incident-details' } }));
  }, []);

  return (
    <div className={'case-report-details-panel' + (theme ? ' ' + theme : '')}>
      <div className="panel-header">Incident Details</div>
      <div className="panel-body">
        {/* Nature of Call */}
        <div className="form-row">
          <div className="form-group form-group-full">
            <label className="field-label required">Incident Nature</label>
            <MultiSelect
              value={report.natureOfCall || []}
              items={natureOfCallOptions}
              disabled={isReadOnly}
              sortItems={false}
              labelKey="value"
              valueKey="code"
              groupBy={natureOfCallGroupBy}
              groupOrder="insertion"
              placeholder="Select nature(s)..."
              onChange={handleNatureChange}
            />
          </div>
        </div>

        {/* Description */}
        <div className="form-row">
          <div className="form-group form-group-full">
            <label className="field-label required">Incident Description (brief)</label>
            <textarea
              value={report.incidentDescription || ''}
              className="form-textarea"
              disabled={isReadOnly}
              onChange={(e) => onFieldChange?.('incidentDescription', e.target.value)}
            />
          </div>
        </div>

        {/* Occurred From & To */}
        <div className="form-row">
          <div className="form-group">
            <label className="field-label">Occurred From</label>
            <DateTimePicker
              value={report.incidentOccurredFrom || null}
              type="datetime"
              format="DD MMM YYYY HH:mm"
              disabled={isReadOnly}
              appendToBody={true}
              popupClass={theme}
              inputAttr={{ class: 'form-input' }}
              open={occurredFromOpen}
              onOpen={handleOccurredFromOpen}
              onOpenChange={setOccurredFromOpen}
              onChange={handleOccurredFromChange}
            />
          </div>
          <div className="form-group">
            <label className="field-label">Occurred To</label>
            <DateTimePicker
              value={report.incidentOccurredTo || null}
              type="datetime"
              format="DD MMM YYYY HH:mm"
              disabled={isReadOnly}
              appendToBody={true}
              popupClass={theme}
              inputAttr={{ class: 'form-input' }}
              open={occurredToOpen}
              onOpen={handleOccurredToOpen}
              onOpenChange={setOccurredToOpen}
              onChange={handleOccurredToChange}
            />
          </div>
        </div>

        {/* Received Via & Received Time */}
        <div className="form-row">
          <div className="form-group">
            <label className="field-label">Received Via</label>
            <Dropdownlist
              value={report.notifiedOfIncidentVia ?? null}
              items={receivedViaOptions}
              labelKey="value"
              valueKey="code"
              placeholder="Select..."
              disabled={isReadOnly}
              searchable={false}
              onChange={(val) => {
                onFieldChange?.('notifiedOfIncidentVia', val);
              }}
            />
          </div>
          <div className="form-group">
            <label className="field-label">Received Time</label>
            <DateTimePicker
              value={report.notifiedOfIncidentTime || null}
              type="datetime"
              format="DD MMM YYYY HH:mm"
              disabled={isReadOnly}
              appendToBody={true}
              popupClass={theme}
              inputAttr={{ class: 'form-input' }}
              open={notifiedTimeOpen}
              onOpen={handleNotifiedTimeOpen}
              onOpenChange={setNotifiedTimeOpen}
              onChange={handleNotifiedTimeChange}
            />
          </div>
        </div>

        {/* Received By & Employee Role */}
        <div className="form-row">
          <div className="form-group">
            <label className="field-label">Received By</label>
            <Dropdownlist
              value={report.notifiedPerson ?? null}
              items={receivedByOptions}
              labelKey="name"
              valueKey="id"
              placeholder="Select..."
              disabled={isReadOnly}
              searchable={true}
              onChange={(val) => {
                onFieldChange?.('notifiedPerson', val);
              }}
            />
          </div>
          <div className="form-group">
            <label className="field-label">Employee Role</label>
            <Dropdownlist
              value={report.notifiedAs ?? null}
              items={employeeRoleOptions}
              labelKey="value"
              valueKey="code"
              placeholder="Select..."
              disabled={isReadOnly}
              searchable={false}
              onChange={(val) => {
                onFieldChange?.('notifiedAs', val);
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

(window as any).CaseReportIncidentDetailsReact = CaseReportIncidentDetails;
